import { Gift, BookMarked, Code2, Landmark, AudioWaveform, Users, Check, ExternalLink } from "lucide-react";
import { useLang } from "../i18n";
import { Reveal, Eyebrow, SectionShell } from "./Reveal";

const icons = [Gift, BookMarked, Code2, Landmark, AudioWaveform, Users];
const images = [
  "/images/roadmap/fase1.jpg",
  "/images/roadmap/fase2.jpg",
  "/images/roadmap/fase4.jpg",
  "/images/roadmap/fase5.jpg",
  "/images/roadmap/fase6.jpg",
  "/images/roadmap/fase7.jpg",
];

export default function Roadmap() {
  const { t } = useLang();

  return (
    <SectionShell id="roadmap" testid="roadmap-section" className="bg-white border-y border-slate-100">
      <Reveal className="text-center">
        <Eyebrow>{t.roadmap.eyebrow}</Eyebrow>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">
          {t.roadmap.title}
        </h2>
        <p className="mx-auto mt-4 max-w-2xl text-slate-600 text-base sm:text-lg leading-relaxed">{t.roadmap.intro}</p>
      </Reveal>

      <div className="mt-14 relative">
        <div className="absolute left-[27px] sm:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-sky-300 via-slate-200 to-transparent" />
        <div className="space-y-10">
          {t.roadmap.items.map((item, i) => {
            const Icon = icons[i];
            const isCurrent = i === 0;
            const isDone = i === 4;
            const right = i % 2 === 1;
            return (
              <Reveal key={i} delay={0.05}>
                <div
                  data-testid={`roadmap-item-${i + 1}`}
                  className={`relative flex gap-6 sm:gap-0 ${right ? "sm:flex-row-reverse" : ""} sm:items-start`}
                >
                  <div className={`hidden sm:block sm:w-1/2 ${right ? "sm:pl-12" : "sm:pr-12"}`}>
                    <div className="relative mt-1">
                      <img
                        src={images[i]}
                        alt={item.title}
                        loading="lazy"
                        data-testid={`roadmap-img-${i + 1}`}
                        className="h-40 w-full rounded-2xl object-cover shadow-sm"
                      />
                      <span
                        data-testid={`roadmap-img-label-${i + 1}`}
                        className="absolute bottom-3 left-3 rounded-full border border-white/30 bg-slate-900/60 px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-white backdrop-blur"
                      >
                        {t.roadmap.labels[i]}
                      </span>
                    </div>
                  </div>
                  <div className="absolute left-[27px] sm:left-1/2 -translate-x-1/2 mt-1">
                    <span
                      className={`grid place-items-center h-14 w-14 rounded-2xl border-2 font-mono font-bold shadow-sm ${
                        isCurrent
                          ? "bg-sky-600 border-sky-600 text-white"
                          : isDone
                          ? "bg-emerald-500 border-emerald-500 text-white"
                          : "bg-white border-slate-200 text-slate-500"
                      }`}
                    >
                      <Icon size={22} />
                    </span>
                  </div>
                  <div
                    className={`ml-16 sm:ml-0 sm:w-1/2 ${right ? "sm:pl-12" : "sm:pr-12"} ${
                      right ? "" : "sm:text-left"
                    }`}
                  >
                    <div
                      className={`rounded-2xl border p-5 sm:p-6 transition-all hover:shadow-lg hover:-translate-y-0.5 ${
                        isCurrent
                          ? "border-sky-200 bg-gradient-to-br from-sky-50 to-white"
                          : "border-slate-200 bg-[#F8FAFC]"
                      }`}
                    >
                      <div className="relative mb-4 sm:hidden">
                        <img
                          src={images[i]}
                          alt={item.title}
                          loading="lazy"
                          data-testid={`roadmap-img-mobile-${i + 1}`}
                          className="h-36 w-full rounded-xl object-cover"
                        />
                        <span
                          data-testid={`roadmap-img-label-mobile-${i + 1}`}
                          className="absolute bottom-3 left-3 rounded-full border border-white/30 bg-slate-900/60 px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-white backdrop-blur"
                        >
                          {t.roadmap.labels[i]}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-mono text-xs font-bold text-slate-400">FASE {i + 1}</span>
                        {isCurrent && (
                          <span
                            data-testid="roadmap-badge-current"
                            className="rounded-full bg-sky-600 px-2.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider text-white"
                          >
                            {t.roadmap.badges.current}
                          </span>
                        )}
                        {isDone && (
                          <span
                            data-testid="roadmap-badge-done"
                            className="rounded-full bg-emerald-500 px-2.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider text-white"
                          >
                            {t.roadmap.badges.done}
                          </span>
                        )}
                        {item.date && (
                          <span className="rounded-full border border-slate-300 px-2.5 py-0.5 font-mono text-[10px] font-bold text-slate-600">
                            {item.date}
                          </span>
                        )}
                      </div>
                      <p className="mt-2.5 text-sm sm:text-base text-slate-700 leading-relaxed">{item.title}</p>
                      {item.subs && (
                        <ul className="mt-3 space-y-1.5">
                          {item.subs.map((s) => (
                            <li key={s.k} className="flex items-start gap-2 text-sm text-slate-600 leading-relaxed">
                              {s.done && <Check size={16} className="mt-0.5 shrink-0 text-emerald-500" />}
                              <span>
                                {s.k && s.href ? (
                                  <a
                                    href={s.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`roadmap-sub-link-${s.k.toLowerCase()}`}
                                    className="inline-flex items-center gap-1 font-semibold text-sky-700 underline underline-offset-2 hover:text-sky-500"
                                  >
                                    {s.k}
                                    <ExternalLink size={12} />
                                  </a>
                                ) : (
                                  s.k && <span className="font-semibold text-slate-900">{s.k}</span>
                                )}
                                {s.k && ": "}
                                {s.v}
                                {s.done && (
                                  <span className="ml-1.5 rounded-full bg-emerald-100 px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider text-emerald-700">
                                    {t.roadmap.badges.done}
                                  </span>
                                )}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                      {item.note && (
                        <p className="mt-3 border-t border-slate-200 pt-3 text-xs sm:text-sm text-slate-500 leading-relaxed">
                          <span className="font-semibold text-slate-700">Note:</span> {item.note}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
      <Reveal delay={0.1} className="mt-14">
        <div className="grid sm:grid-cols-2 gap-5">
          {t.roadmap.goals.map((g, i) => (
            <div
              key={g.badge}
              data-testid={`goal-card-${i + 1}`}
              className={`relative overflow-hidden rounded-3xl p-8 shadow-xl ${
                i === 0
                  ? "bg-gradient-to-br from-amber-400 via-amber-500 to-amber-600 shadow-amber-500/25"
                  : "bg-gradient-to-br from-sky-700 via-sky-800 to-slate-900 shadow-sky-900/25"
              }`}
            >
              <div className="pointer-events-none absolute inset-0 bg-grid-dark opacity-20" />
              <div className="relative">
                <p
                  className={`font-mono text-xs uppercase tracking-[0.25em] ${
                    i === 0 ? "text-amber-100" : "text-sky-200"
                  }`}
                >
                  {g.badge}
                </p>
                <h3 className="mt-3 font-display text-xl sm:text-2xl font-bold text-white">{g.title}</h3>
                <p className={`mt-2 text-sm sm:text-base leading-relaxed ${i === 0 ? "text-amber-50" : "text-sky-100"}`}>
                  {g.text}
                </p>
                <div className="mt-5 inline-block rounded-2xl bg-white/15 border border-white/30 px-6 py-4 backdrop-blur">
                  <p className="font-mono text-4xl font-bold text-white" data-testid={`goal-price-${i + 1}`}>
                    {g.price}
                  </p>
                  <p
                    className={`mt-1 font-mono text-[10px] uppercase tracking-[0.2em] ${
                      i === 0 ? "text-amber-100" : "text-sky-200"
                    }`}
                  >
                    {g.by}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Reveal>
    </SectionShell>
  );
}
